import React, { useRef, useState } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { FileText, GripVertical, Loader2, Trash2, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { MESSAGE_VARIABLES } from "@/lib/messageVariables";
import {
  deleteFluxoMediaByUrl,
  uploadFluxoMedia,
  type FluxoMediaKind,
} from "@/lib/fluxoMedia";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { isFluxoMediaBlock, type FluxoBlock } from "@/hooks/useFluxos";
import CampaignTagSelector from "@/components/campaigns/CampaignTagSelector";
import { BLOCK_META } from "./blockMeta";
import { useTranslation } from "react-i18next";

export interface AssistantOption {
  id: string;
  name: string;
}

interface BlockCardProps {
  block: FluxoBlock;
  assistants: AssistantOption[];
  accountId?: string | null;
  fluxoId?: string | null;
  onChange: (block: FluxoBlock) => void;
  onRemove: (id: string) => void;
}

const MAX_IMAGE_SIZE_MB = 2;
const MAX_VIDEO_SIZE_MB = 15;
const MAX_DOCUMENT_SIZE_MB = 10;
const MAX_AUDIO_SIZE_MB = 15;
const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png"];
const ALLOWED_AUDIO_TYPES = ["audio/mpeg", "audio/mp3"];
// WhatsApp só entrega H.264/AAC de forma confiável — mesma restrição do chat.
const ALLOWED_VIDEO_TYPES = ["video/mp4"];
const ALLOWED_DOCUMENT_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "text/plain",
  "text/csv",
  "application/zip",
  "application/x-rar-compressed",
  "application/x-7z-compressed",
  "application/json",
  "application/xml",
  "application/vnd.oasis.opendocument.text",
  "application/vnd.oasis.opendocument.spreadsheet",
  "application/vnd.oasis.opendocument.presentation",
];
// O navegador reporta "" ou application/octet-stream para .zip/.rar/.csv, então a
// extensão é o critério que realmente decide nesses casos.
const ALLOWED_DOCUMENT_EXTENSIONS = [
  ".pdf", ".doc", ".docx", ".xls", ".xlsx", ".ppt", ".pptx",
  ".txt", ".csv", ".zip", ".rar", ".7z", ".json", ".xml",
  ".odt", ".ods", ".odp",
];
const DOCUMENT_ACCEPT = [...ALLOWED_DOCUMENT_EXTENSIONS, ...ALLOWED_DOCUMENT_TYPES].join(",");

const FLUXO_VARIABLES = MESSAGE_VARIABLES.filter((v) => v.token !== "{{ jid }}");

interface FileUploadFieldProps {
  accept: string;
  label: string;
  fileName?: string;
  hasFile: boolean;
  maxSizeMb: number;
  allowedTypes?: string[];
  /** Quando informado, um match de extensão também libera o arquivo. */
  allowedExtensions?: string[];
  invalidTypeMessage?: string;
  uploading?: boolean;
  disabled?: boolean;
  onFile: (file: File) => void;
  onClear: () => void;
}

const FileUploadField: React.FC<FileUploadFieldProps> = ({
  accept,
  label,
  fileName,
  hasFile,
  maxSizeMb,
  allowedTypes,
  allowedExtensions,
  invalidTypeMessage,
  uploading = false,
  disabled = false,
  onFile,
  onClear,
}) => {
  const { t } = useTranslation("automations");
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (allowedTypes) {
      const typeOk = allowedTypes.includes(file.type);
      const extensionOk = allowedExtensions?.some((ext) =>
        file.name.toLowerCase().endsWith(ext)
      );
      if (!typeOk && !extensionOk) {
        toast.error(invalidTypeMessage || "Formato de arquivo inválido.");
        return;
      }
    }
    if (file.size > maxSizeMb * 1024 * 1024) {
      toast.error(t("flow.fileTooLarge", { size: String(maxSizeMb) }));
      return;
    }
    onFile(file);
  };

  return (
    <div className="flex items-center gap-2">
      <label
        className={`flex flex-1 items-center gap-2 px-3 py-2 rounded-md border border-dashed border-[#CBD5E1] bg-[#F8FAFC] min-w-0 dark:border-neutral-600 dark:bg-neutral-700/50 ${
          disabled || uploading
            ? "opacity-60 cursor-not-allowed"
            : "cursor-pointer hover:border-[#94A3B8] dark:hover:border-neutral-500"
        } transition-colors`}
      >
        {uploading ? (
          <Loader2 className="w-4 h-4 text-[#94A3B8] shrink-0 animate-spin dark:text-neutral-400" />
        ) : (
          <Upload className="w-4 h-4 text-[#94A3B8] shrink-0 dark:text-neutral-400" />
        )}
        <span className="text-sm text-[#64748B] truncate dark:text-neutral-400">
          {uploading ? "Enviando arquivo..." : hasFile ? fileName || "Arquivo selecionado" : label}
        </span>
        <input
          type="file"
          className="hidden"
          accept={accept}
          disabled={disabled || uploading}
          onChange={handleChange}
        />
      </label>
      {hasFile && !uploading && (
        <button
          onClick={onClear}
          className="w-[34px] h-[34px] shrink-0 flex justify-center items-center rounded-[10px] border border-border text-[#64748B] hover:bg-[#F1F5F9] dark:rounded-[8px] dark:border-[#3F3F46] dark:text-neutral-400 dark:hover:bg-[#27272A]"
          aria-label={t("flow.removeFile")}
        >
          <X size={16} />
        </button>
      )}
    </div>
  );
};

const BlockCard: React.FC<BlockCardProps> = ({
  block,
  assistants,
  accountId,
  fluxoId,
  onChange,
  onRemove,
}) => {
  const { t } = useTranslation("automations");
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: block.id });
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [uploading, setUploading] = useState(false);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const insertVariableAtCursor = (token: string) => {
    if (block.type !== "texto") return;
    const el = textareaRef.current;
    if (!el) {
      onChange({ ...block, content: block.content + token });
      return;
    }
    const start = el.selectionStart ?? block.content.length;
    const end = el.selectionEnd ?? block.content.length;
    const next = block.content.slice(0, start) + token + block.content.slice(end);
    onChange({ ...block, content: next });
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + token.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const handleMediaUpload = async (file: File, kind: FluxoMediaKind) => {
    if (!accountId || !fluxoId) {
      toast.error(t("flow.noAccountForUpload"));
      return;
    }

    setUploading(true);
    try {
      if (isFluxoMediaBlock(block)) {
        await deleteFluxoMediaByUrl(block.url);
      }

      const publicUrl = await uploadFluxoMedia({
        accountId,
        fluxoId,
        blockId: block.id,
        file,
        kind,
        fileName: file.name,
      });

      if (block.type === "arquivo") {
        onChange({ ...block, url: publicUrl, fileName: file.name, mimeType: file.type });
      } else if (isFluxoMediaBlock(block)) {
        onChange({ ...block, url: publicUrl, fileName: file.name });
      }
    } catch (err) {
      console.error("💥 [BlockCard] Upload falhou:", err);
      toast.error(err instanceof Error ? err.message : "Erro ao enviar arquivo");
    } finally {
      setUploading(false);
    }
  };

  const handleClearMedia = async () => {
    if (!isFluxoMediaBlock(block)) return;
    await deleteFluxoMediaByUrl(block.url);
    if (block.type === "arquivo") {
      onChange({ ...block, url: "", fileName: undefined, mimeType: undefined });
      return;
    }
    onChange({ ...block, url: "", fileName: undefined });
  };

  const renderContent = () => {
    switch (block.type) {
      case "texto":
        return (
          <div className="flex flex-col gap-2">
            <Textarea
              ref={textareaRef}
              value={block.content}
              onChange={(e) => onChange({ ...block, content: e.target.value })}
              placeholder={t("flow.textPlaceholder")}
              className="min-h-[80px]"
            />
            <div className="flex flex-col gap-1.5">
              <span className="text-xs text-[#64748B] dark:text-neutral-300">{t("flow.variables")}</span>
              <div className="flex flex-wrap gap-1.5">
                {FLUXO_VARIABLES.map((variable) => (
                  <button
                    key={variable.token}
                    type="button"
                    onClick={() => insertVariableAtCursor(variable.token)}
                    title={variable.description}
                    className="text-[11px] font-mono px-2 py-1 rounded bg-[#F1F5F9] text-[#334155] border border-border hover:bg-[#E2E8F0] transition-colors dark:bg-neutral-700/70 dark:text-neutral-200 dark:border-[#3F3F46] dark:hover:bg-neutral-600"
                  >
                    {variable.token}
                  </button>
                ))}
              </div>
            </div>
          </div>
        );
      case "imagem":
        return (
          <div className="flex flex-col gap-2">
            <FileUploadField
              accept="image/jpeg,image/png,.jpg,.jpeg,.png"
              label={t("flow.pickImage")}
              fileName={block.fileName}
              hasFile={Boolean(block.url)}
              maxSizeMb={MAX_IMAGE_SIZE_MB}
              allowedTypes={ALLOWED_IMAGE_TYPES}
              invalidTypeMessage="Formato inválido. Use jpeg, jpg ou png."
              uploading={uploading}
              disabled={!accountId || !fluxoId}
              onFile={(file) => void handleMediaUpload(file, "imagem")}
              onClear={() => void handleClearMedia()}
            />
            {block.url && (
              <img
                src={block.url}
                alt={t("flow.imagePreview")}
                className="max-h-[160px] w-fit rounded-[8px] border border-border object-contain dark:border-[#3F3F46]"
              />
            )}
            <p className="text-xs text-[#64748B] dark:text-neutral-400">
              {t("flow.imageHint")}
            </p>
          </div>
        );
      case "video":
        return (
          <div className="flex flex-col gap-2">
            <FileUploadField
              accept="video/mp4,.mp4"
              label={t("flow.pickVideo")}
              fileName={block.fileName}
              hasFile={Boolean(block.url)}
              maxSizeMb={MAX_VIDEO_SIZE_MB}
              allowedTypes={ALLOWED_VIDEO_TYPES}
              allowedExtensions={[".mp4"]}
              invalidTypeMessage={t("flow.invalidVideoType")}
              uploading={uploading}
              disabled={!accountId || !fluxoId}
              onFile={(file) => void handleMediaUpload(file, "video")}
              onClear={() => void handleClearMedia()}
            />
            {block.url && (
              <video
                controls
                src={block.url}
                className="max-h-[180px] w-fit max-w-full rounded-[8px] border border-border dark:border-[#3F3F46]"
              />
            )}
            <p className="text-xs text-[#64748B] dark:text-neutral-400">
              {t("flow.videoHint")}
            </p>
          </div>
        );
      case "arquivo":
        return (
          <div className="flex flex-col gap-2">
            <FileUploadField
              accept={DOCUMENT_ACCEPT}
              label={t("flow.pickFile")}
              fileName={block.fileName}
              hasFile={Boolean(block.url)}
              maxSizeMb={MAX_DOCUMENT_SIZE_MB}
              allowedTypes={ALLOWED_DOCUMENT_TYPES}
              allowedExtensions={ALLOWED_DOCUMENT_EXTENSIONS}
              invalidTypeMessage={t("flow.invalidFileType")}
              uploading={uploading}
              disabled={!accountId || !fluxoId}
              onFile={(file) => void handleMediaUpload(file, "arquivo")}
              onClear={() => void handleClearMedia()}
            />
            {block.url && (
              <a
                href={block.url}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-2 w-fit max-w-full px-3 py-2 rounded-[8px] border border-border bg-white text-sm text-[#0F172A] hover:bg-[#F1F5F9] transition-colors dark:border-[#3F3F46] dark:bg-[#27272A] dark:text-neutral-50 dark:hover:bg-[#1f1f23]"
              >
                <FileText size={16} className="shrink-0 text-[#64748B] dark:text-neutral-400" />
                <span className="truncate">{block.fileName || t("flow.blockFile")}</span>
              </a>
            )}
            <p className="text-xs text-[#64748B] dark:text-neutral-400">
              {t("flow.fileHint")}
            </p>
          </div>
        );
      case "audio":
        return (
          <div className="flex flex-col gap-2">
            <FileUploadField
              accept="audio/mpeg,.mp3"
              label={t("flow.pickAudio")}
              fileName={block.fileName}
              hasFile={Boolean(block.url)}
              maxSizeMb={MAX_AUDIO_SIZE_MB}
              allowedTypes={ALLOWED_AUDIO_TYPES}
              invalidTypeMessage="Formato inválido. Use .mp3."
              uploading={uploading}
              disabled={!accountId || !fluxoId}
              onFile={(file) => void handleMediaUpload(file, "audio")}
              onClear={() => void handleClearMedia()}
            />
            {block.url && (
              <audio controls src={block.url} className="w-full max-w-[400px] h-[36px]" />
            )}
            <p className="text-xs text-[#64748B] dark:text-neutral-400">
              {t("flow.audioHint")}
            </p>
          </div>
        );
      case "delay":
        return (
          <div className="flex items-center gap-2">
            <Input
              type="number"
              min={1}
              value={block.seconds}
              onChange={(e) =>
                onChange({ ...block, seconds: Math.max(1, Number(e.target.value) || 1) })
              }
              className="w-[120px]"
            />
            <span className="text-sm text-[#64748B] dark:text-neutral-400">{t("flow.delaySeconds")}</span>
          </div>
        );
      case "assistente":
        return (
          <div className="w-full min-w-0 max-w-full">
            <Select
              value={block.assistantId || undefined}
              onValueChange={(value) => onChange({ ...block, assistantId: value })}
            >
              <SelectTrigger className="bg-white border-border text-[#0F172A] w-full max-w-full min-w-0 dark:bg-[#27272A] dark:border-[#3F3F46] dark:text-neutral-50 [&>span]:min-w-0 [&>span]:truncate">
                <SelectValue placeholder={t("common.pickAssistant")} />
              </SelectTrigger>
              <SelectContent className="bg-white border-border dark:bg-[#18181B] dark:border-[#3F3F46] max-w-[min(100vw-2rem,360px)]">
                {assistants.length === 0 ? (
                  <div className="px-3 py-2 text-sm text-[#64748B] dark:text-neutral-400">
                    {t("flow.noAssistantAvailable")}
                  </div>
                ) : (
                  assistants.map((assistant) => (
                    <SelectItem key={assistant.id} value={assistant.id} className="max-w-full">
                      <span className="block truncate">{assistant.name}</span>
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>
        );
      case "tag":
        return (
          <div className="flex flex-col gap-2">
            <CampaignTagSelector
              selectedTags={block.tags}
              onTagsChange={(tags) => onChange({ ...block, tags })}
              label={null}
            />
            <p className="text-xs text-[#64748B] dark:text-neutral-400">
              {t("flow.tagHint")}
            </p>
          </div>
        );
    }
  };

  const meta = BLOCK_META[block.type];

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="rounded-[12px] border border-border bg-[#F8FAFC] p-4 min-w-0 overflow-hidden dark:border-[#3F3F46] dark:bg-[#18181B]"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-[#0F172A] dark:text-neutral-50">
          <button
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing touch-none text-[#94A3B8] hover:text-[#64748B] dark:text-neutral-500 dark:hover:text-neutral-300"
            aria-label={t("common.reorder")}
          >
            <GripVertical size={18} />
          </button>
          {meta.icon}
          <span className="text-sm font-semibold">{meta.label}</span>
        </div>
        <button
          onClick={() => onRemove(block.id)}
          className="w-[30px] h-[30px] flex justify-center items-center rounded-[8px] text-red-500 hover:bg-red-50 dark:text-red-400 dark:hover:bg-[#27272A]"
          aria-label={t("flow.removeBlock")}
        >
          <Trash2 size={16} />
        </button>
      </div>
      {renderContent()}
    </div>
  );
};

export default BlockCard;
